// ─────────────────────────────────────────────────────────────────────────────
// adventure_renderer.ts — Canvas renderers for adventure-mode page overlays
//
// Draws environmental texture (time/weather/stress), bookmark cards,
// character sheet, and dialog history onto the page canvas.
// ─────────────────────────────────────────────────────────────────────────────

import type { AdventureEnv, AdventureBookmark, AdventureState, DialogEntry } from '@shared/adventure_model';

// ── Environmental texture overlay ────────────────────────────────────────────

/** Time-of-day tint applied AFTER parchment, BEFORE ink. */
function todTint(timeOfDay: number): string {
  // 0 midnight → 0.25 dawn → 0.5 noon → 0.75 dusk → 1 midnight
  const t = timeOfDay;
  if (t < 0.2)  return 'rgba(20, 15, 40, 0.18)';    // deep night
  if (t < 0.3)  return 'rgba(180, 130, 60, 0.08)';   // dawn gold
  if (t < 0.7)  return 'rgba(255, 255, 240, 0.02)';  // daylight — nearly clear
  if (t < 0.8)  return 'rgba(180, 100, 40, 0.10)';   // dusk amber
  return 'rgba(20, 15, 40, 0.16)';                    // evening
}

/** Structural stress pattern — faint grid that appears as the Whole notices. */
function drawStressOverlay(
  ctx: CanvasRenderingContext2D,
  stress: number,
  w: number, h: number,
): void {
  if (stress < 0.05) return;
  ctx.save();
  ctx.globalAlpha = stress * 0.15;
  ctx.strokeStyle = '#4a3a2a';
  ctx.lineWidth = 0.3;
  const spacing = Math.max(12, 40 - stress * 30);  // tighter grid at higher stress
  for (let x = 0; x < w; x += spacing) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
    ctx.stroke();
  }
  for (let y = 0; y < h; y += spacing) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
    ctx.stroke();
  }
  ctx.restore();
}

/** Weather effect — drawn on top of the time tint. */
function drawWeather(
  ctx: CanvasRenderingContext2D,
  weather: AdventureEnv['weather'],
  w: number, h: number,
  t: number,
): void {
  if (weather === 'clear') return;

  ctx.save();
  if (weather === 'rain') {
    ctx.globalAlpha = 0.06;
    ctx.strokeStyle = '#607080';
    ctx.lineWidth = 0.5;
    const seed = Math.floor(t / 80);
    for (let i = 0; i < 40; i++) {
      const x = ((seed * 37 + i * 73) % (w | 1));
      const y = ((seed * 13 + i * 51) % (h | 1));
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x - 2, y + 12);
      ctx.stroke();
    }
  } else if (weather === 'fog') {
    // Gradient from edges — reduces clarity
    const g = ctx.createRadialGradient(w / 2, h / 2, w * 0.2, w / 2, h / 2, w * 0.7);
    g.addColorStop(0, 'rgba(200, 190, 170, 0)');
    g.addColorStop(1, 'rgba(200, 190, 170, 0.25)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
  } else if (weather === 'storm') {
    // Periodic lightning flash
    const flash = Math.sin(t * 0.005) > 0.97;
    if (flash) {
      ctx.globalAlpha = 0.08;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
    }
    // Rain (heavier)
    ctx.globalAlpha = 0.08;
    ctx.strokeStyle = '#506070';
    ctx.lineWidth = 0.7;
    const seed = Math.floor(t / 60);
    for (let i = 0; i < 60; i++) {
      const x = ((seed * 41 + i * 67) % (w | 1));
      const y = ((seed * 17 + i * 43) % (h | 1));
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x - 3, y + 16);
      ctx.stroke();
    }
  }
  ctx.restore();
}

/**
 * Draw the environmental overlay for an adventure page.
 * Called after parchment texture, before ink strokes.
 */
export function drawAdventureEnv(
  ctx: CanvasRenderingContext2D,
  env: AdventureEnv,
  pageW: number, pageH: number,
  t: number,
): void {
  ctx.save();

  // 1. Time-of-day tint
  ctx.fillStyle = todTint(env.timeOfDay);
  ctx.fillRect(0, 0, pageW, pageH);

  // 2. Weather effects
  drawWeather(ctx, env.weather, pageW, pageH, t);

  // 3. Proximity — warm margin tint near Laura
  if (env.proximity.includes('laura')) {
    ctx.fillStyle = 'rgba(200, 160, 100, 0.06)';
    ctx.fillRect(0, 0, pageW, pageH);
  }
  // Cold margin near Ring
  if (env.proximity.includes('ring')) {
    ctx.fillStyle = 'rgba(80, 90, 120, 0.08)';
    ctx.fillRect(0, 0, pageW, pageH);
  }

  // 4. Structural stress grid
  drawStressOverlay(ctx, env.stress, pageW, pageH);

  ctx.restore();
}

// ── Bookmark card renderer ───────────────────────────────────────────────────

const SIGIL_LABELS: Record<string, string> = {
  crown: 'Crown',
  threshold: 'Threshold',
  foundation: 'Foundation',
  root: 'Root',
};

const SIGIL_FRAME_COLORS: Record<string, string> = {
  crown: '#c9a040',       // gold
  threshold: '#8a8a9a',   // silver
  foundation: '#6a5040',  // bronze
  root: '#4a3a2a',        // dark earth
};

/**
 * Draw a single bookmark as a tarot-style card.
 */
export function drawBookmarkCard(
  ctx: CanvasRenderingContext2D,
  bookmark: AdventureBookmark,
  x: number, y: number,
  w: number, h: number,
): void {
  ctx.save();

  // Card background
  const isDormant = bookmark.state === 'dormant';
  const isSpent = bookmark.state === 'spent';
  const isCorrupted = bookmark.state === 'corrupted';

  ctx.fillStyle = isDormant ? '#d8c8a0' : isSpent ? '#b0a080' : isCorrupted ? '#8a6a6a' : '#e8d8b8';
  ctx.globalAlpha = isSpent ? 0.6 : 1;

  // Rounded rect
  const r = 6;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
  ctx.fill();

  // Border
  ctx.strokeStyle = bookmark.state === 'active' ? '#c9a040' : '#8a7a5a';
  ctx.lineWidth = bookmark.state === 'active' ? 2 : 1;
  ctx.stroke();

  // Active glow
  if (bookmark.state === 'active') {
    ctx.shadowColor = '#c9a040';
    ctx.shadowBlur = 8;
    ctx.stroke();
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
  }

  // Card title
  ctx.fillStyle = '#3a2a10';
  ctx.font = 'bold 11px serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText(bookmark.name, x + w / 2, y + 8);

  // Four sigil slots
  const slotH = (h - 40) / 4;
  const slotW = w - 20;
  const slotX = x + 10;

  for (let i = 0; i < 4; i++) {
    const sigil = bookmark.sigils[i];
    const sy = y + 28 + i * slotH;
    const frameColor = SIGIL_FRAME_COLORS[sigil.position] ?? '#6a5a4a';

    // Slot frame
    ctx.strokeStyle = frameColor;
    ctx.lineWidth = i === 0 ? 1.5 : 0.8;  // Crown is most decorated
    ctx.setLineDash(sigil.arcaneId ? [] : [3, 3]);
    ctx.strokeRect(slotX, sy, slotW, slotH - 4);
    ctx.setLineDash([]);

    // Position label
    ctx.fillStyle = '#6a5a4a';
    ctx.font = '8px serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(SIGIL_LABELS[sigil.position] ?? sigil.position, slotX + 3, sy + 2);

    // Sigil content (placeholder — arcane glyph goes here)
    if (sigil.arcaneId) {
      ctx.fillStyle = '#4a3060';
      ctx.font = 'bold 14px serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      // For now, render the arcane ID as text; later, render the glyph
      ctx.fillText(sigil.arcaneId, slotX + slotW / 2, sy + slotH / 2 - 2);

      // Charge bar
      if (sigil.charge > 0) {
        ctx.fillStyle = 'rgba(106, 76, 147, 0.3)';
        ctx.fillRect(slotX + 2, sy + slotH - 7, (slotW - 4) * sigil.charge, 3);
      }
    }
  }

  ctx.globalAlpha = 1;
  ctx.restore();
}

// ── Character sheet renderer ─────────────────────────────────────────────────

export function drawCharacterSheet(
  ctx: CanvasRenderingContext2D,
  state: AdventureState,
  pageW: number, pageH: number,
): void {
  ctx.save();

  const mx = 50;  // left margin
  let cy = 50;    // cursor y

  // Title
  ctx.fillStyle = '#3a2a10';
  ctx.font = 'bold 18px serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText('Character', mx, cy);
  cy += 30;

  // Known arcanes
  ctx.font = 'bold 12px serif';
  ctx.fillText('Known Arcanes', mx, cy);
  cy += 18;

  if (state.knownArcanes.length === 0) {
    ctx.font = 'italic 10px serif';
    ctx.fillStyle = '#8a7a5a';
    ctx.fillText('None learned yet.', mx + 10, cy);
    cy += 16;
  } else {
    ctx.font = '10px serif';
    ctx.fillStyle = '#4a3a2a';
    const cols = 3;
    const colW = (pageW - mx * 2) / cols;
    for (let i = 0; i < state.knownArcanes.length; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      ctx.fillText(`\u25C6 ${state.knownArcanes[i]}`, mx + 10 + col * colW, cy + row * 14);
    }
    cy += Math.ceil(state.knownArcanes.length / cols) * 14 + 8;
  }

  // Status
  ctx.fillStyle = '#3a2a10';
  ctx.font = 'bold 12px serif';
  ctx.fillText('Status', mx, cy);
  cy += 18;

  if (state.env.status.length === 0) {
    ctx.font = 'italic 10px serif';
    ctx.fillStyle = '#8a7a5a';
    ctx.fillText('No active conditions.', mx + 10, cy);
  } else {
    ctx.font = '10px serif';
    ctx.fillStyle = '#4a3a2a';
    for (const s of state.env.status) {
      ctx.fillText(`\u2022 ${s}`, mx + 10, cy);
      cy += 14;
    }
  }
  cy += 20;

  // Environment summary
  ctx.fillStyle = '#3a2a10';
  ctx.font = 'bold 12px serif';
  ctx.fillText('Environment', mx, cy);
  cy += 18;
  ctx.font = '10px serif';
  ctx.fillStyle = '#4a3a2a';
  const tod = state.env.timeOfDay;
  const timeLabel = tod < 0.2 ? 'Night' : tod < 0.3 ? 'Dawn' : tod < 0.7 ? 'Day' : tod < 0.8 ? 'Dusk' : 'Evening';
  ctx.fillText(`Time: ${timeLabel}`, mx + 10, cy); cy += 14;
  ctx.fillText(`Weather: ${state.env.weather}`, mx + 10, cy); cy += 14;
  ctx.fillText(`Stress: ${(state.env.stress * 100).toFixed(0)}%`, mx + 10, cy); cy += 14;

  // Bookmarks summary
  cy += 10;
  ctx.fillStyle = '#3a2a10';
  ctx.font = 'bold 12px serif';
  ctx.fillText(`Bookmarks (${state.bookmarks.length})`, mx, cy);
  cy += 18;
  ctx.font = '10px serif';
  ctx.fillStyle = '#4a3a2a';
  for (const bm of state.bookmarks) {
    ctx.fillText(`\u25C7 ${bm.name} [${bm.state}]`, mx + 10, cy);
    cy += 14;
  }

  ctx.restore();
}

// ── Dialog history renderer ──────────────────────────────────────────────────

const SPEAKER_STYLES: Record<string, { font: string; color: string; align: CanvasTextAlign }> = {
  ireth:  { font: '10px "Georgia", serif', color: '#3a2a10', align: 'left' },
  laura:  { font: '10px "Segoe UI", sans-serif', color: '#5a3a20', align: 'left' },
  system: { font: 'italic 9px serif', color: '#8a7a5a', align: 'center' },
};

export function drawDialogHistory(
  ctx: CanvasRenderingContext2D,
  log: DialogEntry[],
  pageW: number, pageH: number,
): void {
  ctx.save();

  const mx = 50;
  let cy = 50;

  ctx.fillStyle = '#3a2a10';
  ctx.font = 'bold 18px serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText('Dialog History', mx, cy);
  cy += 30;

  if (log.length === 0) {
    ctx.font = 'italic 10px serif';
    ctx.fillStyle = '#8a7a5a';
    ctx.fillText('No conversations yet.', mx + 10, cy);
    ctx.restore();
    return;
  }

  // Show last N entries that fit on the page
  const lineH = 16;
  const maxEntries = Math.floor((pageH - cy - 40) / (lineH * 2));
  const start = Math.max(0, log.length - maxEntries);
  const visible = log.slice(start);

  for (const entry of visible) {
    if (cy > pageH - 60) break;

    const style = SPEAKER_STYLES[entry.speaker] ?? SPEAKER_STYLES.system;

    // Speaker label
    ctx.fillStyle = '#8a7a5a';
    ctx.font = 'bold 8px serif';
    ctx.textAlign = 'left';
    const label = entry.speaker.charAt(0).toUpperCase() + entry.speaker.slice(1);
    ctx.fillText(label, mx, cy);
    cy += 12;

    // Text
    ctx.fillStyle = style.color;
    ctx.font = style.font;
    ctx.textAlign = style.align;
    const textX = style.align === 'center' ? pageW / 2 : mx + 10;

    // Simple word-wrap
    const maxW = pageW - mx * 2 - 20;
    const words = entry.text.split(' ');
    let line = '';
    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      if (ctx.measureText(test).width > maxW && line) {
        ctx.fillText(line, textX, cy);
        cy += lineH;
        line = word;
      } else {
        line = test;
      }
    }
    if (line) {
      ctx.fillText(line, textX, cy);
      cy += lineH;
    }
    cy += 6;
  }

  ctx.restore();
}

// ── Bookmarks page renderer ─────────────────────────────────────────────────

export function drawBookmarksPage(
  ctx: CanvasRenderingContext2D,
  bookmarks: AdventureBookmark[],
  pageW: number, pageH: number,
): void {
  ctx.save();

  const mx = 40;
  let cy = 50;

  ctx.fillStyle = '#3a2a10';
  ctx.font = 'bold 18px serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText('Chapter Bookmarks', mx, cy);
  cy += 30;

  if (bookmarks.length === 0) {
    ctx.font = 'italic 11px serif';
    ctx.fillStyle = '#8a7a5a';
    ctx.fillText('No bookmarks acquired yet.', mx + 10, cy);
    cy += 20;
    ctx.font = '10px serif';
    ctx.fillText('Draw a bookmark glyph here to acquire it.', mx + 10, cy);
    ctx.restore();
    return;
  }

  // Lay out bookmark cards in a grid
  const cardW = 140;
  const cardH = 240;
  const gap = 16;
  const cols = Math.max(1, Math.floor((pageW - mx * 2) / (cardW + gap)));

  for (let i = 0; i < bookmarks.length; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cx = mx + col * (cardW + gap);
    const cardY = cy + row * (cardH + gap);
    if (cardY + cardH > pageH - 20) break;  // don't overflow page

    drawBookmarkCard(ctx, bookmarks[i], cx, cardY, cardW, cardH);
  }

  ctx.restore();
}
